import { ChangeEvent, useCallback, useState } from 'react';
import FormField from './FormField';
import {
  Button,
  Container,
  ErrorMessage,
  Heading,
  ResultItem,
  ResultsContainer,
  SecondaryButton,
  Select,
  SmallSecondaryButton,
  Text,
  TextStrong
} from './PassengerTools.styles';
import { copyToClipboard } from './utils/createTestUser';
import {
  InventoryType,
  InventorySearchResult,
  findInventoryVehicles,
  isZipCodeInventoryType
} from './utils/findInventoryType';

type ZipType = 'DeliveryUnavailable' | 'OutOfMarket';

const zipTypeOptions: Array<{
  value: ZipType;
  label: string;
  description: string;
}> = [
  {
    value: 'OutOfMarket',
    label: 'Out of Market',
    description: 'Zip codes that are out of market'
  },
  {
    value: 'DeliveryUnavailable',
    label: 'Delivery Unavailable',
    description: 'Zip codes where delivery is temporarily unavailable'
  }
];

export default function ZipCodes() {
  const [selectedZipType, setSelectedZipType] =
    useState<ZipType>('OutOfMarket');
  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchResults, setSearchResults] = useState<InventorySearchResult[]>(
    []
  );

  const handleSearch = useCallback(async () => {
    setIsSearching(true);
    setErrorMessage('');

    try {
      const searchResult = await findInventoryVehicles({
        inventoryType: selectedZipType as InventoryType
      });
      setSearchResults(prev => [...prev, searchResult]);
    } catch (error) {
      console.error('Zip code search error:', error);
      setErrorMessage(
        `Failed to search ${selectedZipType} zip codes: ${error}`
      );
    } finally {
      setIsSearching(false);
    }
  }, [selectedZipType]);

  if (searchResults.length > 0) {
    return (
      <Container>
        <Heading>Zip Code Search Results</Heading>

        <ResultsContainer>
          {searchResults.map((result, index) => (
            <ResultItem key={index}>
              <Text className="mb-sm">
                <TextStrong>Zip Code Type:</TextStrong> {result.inventoryType}
              </Text>

              <Text className="mb-sm">
                <TextStrong>Total Results:</TextStrong>{' '}
                {result.totalResults.toLocaleString()}
              </Text>

              <Text className="mb-sm">
                <TextStrong>Searched At:</TextStrong>{' '}
                {new Date(result.searchedAt).toLocaleString()}
              </Text>

              {isZipCodeInventoryType(result.inventoryType) &&
                result.zipCodes &&
                result.zipCodes.length > 0 && (
                  <div>
                    <Text className="mb-sm text-white">
                      <strong>Zip Codes:</strong>
                    </Text>
                    <div className="grid max-h-[200px] grid-cols-4 gap-sm overflow-y-auto">
                      {result.zipCodes.map((zipCode, zipIndex) => (
                        <div
                          key={zipIndex}
                          className="flex items-center gap-xs text-gray-300 t-body-xs"
                        >
                          <span className="font-mono">{zipCode.zipCode}</span>
                          <SmallSecondaryButton
                            onClick={() => copyToClipboard(zipCode.zipCode)}
                            data-analytics-id={null}
                            title="Copy zip code"
                            data-testid="copy-zip-code"
                          >
                            Copy
                          </SmallSecondaryButton>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </ResultItem>
          ))}
        </ResultsContainer>

        <div className="flex justify-end pt-md">
          <SecondaryButton
            type="button"
            onClick={() => setSearchResults([])}
            data-analytics-id={null}
            data-testid="clear-results"
          >
            Clear
          </SecondaryButton>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Heading>Find Zip Codes</Heading>

      <FormField
        label="Zip Code Type"
        htmlFor="zip-type"
        helperText={
          zipTypeOptions.find(opt => opt.value === selectedZipType)?.description
        }
      >
        <Select
          id="zip-type"
          value={selectedZipType}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            setSelectedZipType(e.target.value as ZipType)
          }
        >
          {zipTypeOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </FormField>

      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

      <div className="flex justify-end pt-md">
        <Button
          type="button"
          onClick={handleSearch}
          data-analytics-id={null}
          disabled={isSearching}
          data-testid="find-zip-codes"
        >
          {isSearching ? 'Searching...' : 'Find Zip Codes'}
        </Button>
      </div>
    </Container>
  );
}
